import type {
  NetworkCostDescriptor,
  PlanInfoBenefitCategory,
  PlanInfoBenefitService,
} from "../../@types";
import { stringUnionToArray } from "../typeHelpers";

/**
 * Array of every possible benefit service name from MCTAPI
 * @see `PlanInfoBenefitService` to confirm with Swagger or MCTAPI code
 */
export const planInfoBenefitServices =
  stringUnionToArray<PlanInfoBenefitService>()(
    "SERVICE_NOT_SPECIFIED",
    "SERVICE_PRIMARY",
    "SERVICE_SPECIALIST",
    "SERVICE_EMERGENCY",
    "SERVICE_URGENT_CARE",
    "SERVICE_DIAGNOSTIC_TESTS",
    "SERVICE_LAB_SERVICES",
    "SERVICE_DIAGNOSTIC_RADIOLOGY_SERVICES",
    "SERVICE_OUTPATIENT_XRAYS",
    "SERVICE_HEARING_EXAM",
    "SERVICE_FITTING_EVALUATION",
    "SERVICE_HEARING_AIDS",
    "SERVICE_HEARING_AIDS_INNER_EAR",
    "SERVICE_HEARING_AIDS_OUTER_EAR",
    "SERVICE_HEARING_AIDS_OVER_THE_EAR",
    "SERVICE_OFFICE_VISIT",
    "SERVICE_ORAL_EXAM",
    "SERVICE_CLEANING",
    "SERVICE_FLUORIDE_TREATMENT",
    "SERVICE_DENTAL_XRAYS",
    "SERVICE_MEDICARE_COVERED_COMPREHENSIVE_DENTAL_SERVICES",
    "SERVICE_NONROUTINE_SERVICES",
    "SERVICE_DIAGNOSTIC_SERVICES",
    "SERVICE_RESTORATIVE_SERVICES",
    "SERVICE_ENDODONTICS",
    "SERVICE_PERIODONTICS",
    "SERVICE_EXTRACTIONS",
    "SERVICE_PROSTHODONTICS_AND_OTHER_SERVICES",
    "SERVICE_MEDICARE_COVERED_EYE_EXAM",
    "SERVICE_ROUTINE_EYE_EXAM",
    "SERVICE_OTHER",
    "SERVICE_MEDICARE_COVERED_EYEWEAR",
    "SERVICE_CONTACT_LENSES",
    "SERVICE_EYEGLASSES_FRAMES_AND_LENSES",
    "SERVICE_EYEGLASS_FRAMES",
    "SERVICE_EYEGLASS_LENSES",
    "SERVICE_UPGRADES",
    "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT_WITH_PSYCHIATRIST",
    "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT_WITH_PSYCHIATRIST",
    "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT",
    "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT",
    "SERVICE_OCCUPATIONAL_THERAPY_VISIT",
    "SERVICE_PHYSICAL_THERAPY_AND_SPEECH_AND_LANGUAGE_THERAPY_VISIT",
    "SERVICE_FOOT_EXAMS_AND_TREATMENT",
    "SERVICE_ROUTINE_FOOT_CARE",
    "SERVICE_DURABLE_MEDICAL_EQUIPMENT",
    "SERVICE_PROSTHETICS",
    "SERVICE_DIABETES_SUPPLIES",
    "SERVICE_CHEMOTHERAPY",
    "SERVICE_OTHER_PART_B_DRUGS",
    "SERVICE_MONTHLY_PREMIUM",
    "SERVICE_DEDUCTIBLE",
    "SERVICE_COST_PLAN_CHEMOTHERAPY",
    "SERVICE_COST_PLAN_OTHER_PART_B_DRUGS",
    "SERVICE_PART_B_INSULIN",
    "MEDICARE_COVERED_BENEFITS",
    "ROUTINE_HEARING_EXAMS",
    "FITTING_EVALUATION_HEARING_AIDS",
    "OVER_EAR_HEARING_AIDS_SINGLE",
    "OVER_EAR_HEARING_AIDS_BOTH",
    "OUTER_EAR_HEARING_AIDS_SINGLE",
    "OUTER_EAR_HEARING_AIDS_BOTH",
    "INNER_EAR_HEARING_AIDS_SINGLE",
    "INNER_EAR_HEARING_AIDS_BOTH",
    "ALL_HEARING_AIDS",
    "RX_HEARING_AIDS",
    "OTC_HEARING_AIDS",
    "OTHER_DIAGNOSTIC_SERVICES",
    "PROPHYLAXIS",
    "OTHER_PREVENTATIVE_DENTAL_SERVICES",
    "PROSTHODONTICS_REMOVABLE",
    "PROSTHODONTICS_FIXED",
    "MAXILLOFACIAL_PROSTHETICS",
    "IMPLANT_SERVICES",
    "ORAL_AND_MAXILLOFACIAL_SURGERY",
    "ORTHODONTICS",
    "ADJUNCTIVE_GENERAL_SERVICES",
    "VISION_ROUTINE_EYE_EXAMS",
    "VISION_CONTACT_LENSES",
    "VISION_EYEGLASSES_FRAMES",
    "VISION_EYEGLASSES_LENSES",
    "VISION_EYEGLASSES_FRAMES_AND_LENSES",
    "VISION_UPGRADES",
    "SERVICE_OPIOID_TREATMENT_PROGRAM_SERVICES",
    "SERVICE_OUTPATIENT_HOSPITAL_SERVICES",
    "GROUND_AMBULANCE",
    "PART_B_CHEMOTHERAPY_DRUGS",
    "PART_B_OTHER_DRUGS",
    "SERVICE_DIALYSIS",
    "SERVICE_MEDICARE_COVERED_ZERO_DOLLAR_PREVENTIVE_SERVICES",
    "SKILLED_NURSING_FACILITY",
    "INPATIENT_HOSPITAL",
    "CHIROPRACTIC",
    "TELEHEALTH",
    "NON_EMERGENCY_CARE_PLAN_APPROVED",
    "NON_EMERGENCY_CARE_ANY_HEALTH_LOCATION",
    "ACUPUNCTURE",
    "OTC_ITEMS",
    "MEALS_SHORT_DURATION",
    "ANNUAL_PHYSICAL",
    "HEALTH_EDUCATION",
    "FITNESS",
    "HOME_BATHROOM_SAFETY",
    "COUNSELING",
    "PERS",
    "POST_DISCHARGE_MED_REC",
    "MASSAGE",
    "ALT_THERAPIES",
    "READMISSION_PREVENTION",
    "WIGS_CHEMO_RELATED",
    "WEIGHT_MANAGEMENT",
    "ADULT_DAY_HEALTH",
    "HOME_PALLIATIVE_CARE",
    "IN_HOME_SUPPORT",
    "CAREGIVER_SUPPORT",
    "SERVICE_WORLDWIDE_EMERGENCY",
    "FOOD_PRODUCE",
    "MEALS",
    "PEST_CONTROL",
    "TRANSPORTATION_NON_MED_ANY",
    "TRANSPORTATION_NON_MED_PLAN_APPROVED",
    "AIR_QUALITY_EQUIPMENT",
    "SOCIAL_NEEDS",
    "COMPLEMENTARY_THERAPIES",
    "SUPPORT_SELF_DIRECTION",
    "STRUCTURAL_HOME_MODS",
    "GENERAL_SUPPORTS",
    "NUTRITIONAL_DIETARY_BENEFIT",
    "ADDL_SMOKING_AND_TOBACCO_CESSATION_COUNSELING",
    "ENHANCED_DISEASE_MANAGEMENT",
    "TELEMONITORING_SERVICES",
    "REMOTE_ACCESS_TECHNOLOGIES_WEB_PHONE_BASED",
    "REMOTE_ACCESS_TECHNOLOGIES_NURSING_HOTLINE",
    "IN_HOME_SAFETY_ASSESSMENT",
    "MEDICAL_NUTRITION_THERAPY_MNT"
  );

/**
 * Array of every possible benefit category name from MCTAPI
 * @see `PlanInfoBenefitCategory` to confirm with Swagger or MCTAPI code
 */
export const planInfoBenefitCategories =
  stringUnionToArray<PlanInfoBenefitCategory>()(
    "BENEFIT_NOT_SPECIFIED",
    "BENEFIT_AMBULANCE",
    "BENEFIT_PREVENTIVE_DENTAL",
    "BENEFIT_COMPREHENSIVE_DENTAL",
    "BENEFIT_DIAGNOSTIC_PROCEDURES",
    "BENEFIT_DOCTOR_VISITS",
    "BENEFIT_MEDICAL_EQUIPMENT",
    "BENEFIT_EMERGENCY_CARE",
    "BENEFIT_FOOT_CARE",
    "BENEFIT_HEARING",
    "BENEFIT_MENTAL_HEALTH",
    "BENEFIT_REHABILITATION_SERVICES",
    "BENEFIT_OUTPATIENT_HOSPITAL",
    "BENEFIT_TRANSPORTATION",
    "BENEFIT_VISION",
    "BENEFIT_PREVENTIVE_CARE",
    "BENEFIT_INPATIENT_HOSPITAL",
    "BENEFIT_SKILLED_NURSING_FACILITY",
    "BENEFIT_OUTPATIENT_PRESCRIPTION_DRUGS",
    "BENEFIT_OPTIONAL_BENEFITS",
    "BENEFIT_WELLNESS_PROGRAMS",
    "BENEFIT_MEDICARE_PART_B",
    "BENEFIT_PACKAGE_1",
    "BENEFIT_PACKAGE_2",
    "BENEFIT_PACKAGE_3",
    "BENEFIT_PACKAGE_4",
    "BENEFIT_PACKAGE_5",
    "BENEFIT_PACKAGE_6",
    "BENEFIT_PACKAGE_7",
    "BENEFIT_PACKAGE_8",
    "BENEFIT_PACKAGE_9",
    "BENEFIT_OTHER_DEDUCTIBLES",
    "BENEFIT_MAXIMUM_OOPC",
    "BENEFIT_OPTIONAL_SUPPLEMENTAL_BENEFITS",
    "BENEFIT_HEALTH_PLAN_DEDUCTIBLE",
    "HEARING_EXAMS",
    "HEARING_AIDS",
    "OTHER_SERVICES",
    "BENEFIT_MEDICARE_COVERED_ZERO_COSTSHARING_PREVENTIVE_SERVICES",
    "PART_B_DRUGS",
    "PROFESSIONAL_SERVICES",
    "PREVENTIVE_SERVICES",
    "BENEFITS_CHRONICALLY_ILL"
  );

export const PLAN_HAS_NO_LIMITS: NetworkCostDescriptor = {
  authorization_required: false,
  referral_required: false,
  plan_limits: false,
  cost_share: "",
};

export const maBenefitsTieredCostSharingClassname = "mct-c-tiered-cost-sharing";

export const servicesToUseMaBenefits: PlanInfoBenefitService[] = [
  "GROUND_AMBULANCE",
  // The following 2 service names are duplicates
  // "SERVICE_CHEMOTHERAPY", // found only in `package_benefits`, but including here for reference
  "PART_B_CHEMOTHERAPY_DRUGS", // found in `ma_benefits`
  "SERVICE_DIABETES_SUPPLIES",
  "SERVICE_DIAGNOSTIC_RADIOLOGY_SERVICES",
  "SERVICE_DIAGNOSTIC_TESTS",
  "SERVICE_DIALYSIS",
  "SERVICE_DURABLE_MEDICAL_EQUIPMENT",
  "SERVICE_EMERGENCY",
  "SERVICE_LAB_SERVICES",
  "SERVICE_MEDICARE_COVERED_ZERO_DOLLAR_PREVENTIVE_SERVICES",
  "SERVICE_OCCUPATIONAL_THERAPY_VISIT",
  "SERVICE_OPIOID_TREATMENT_PROGRAM_SERVICES",
  // The following 2 service names are duplicates
  // "SERVICE_OTHER_PART_B_DRUGS", // found only in `package_benefits`, but including here for reference
  "PART_B_OTHER_DRUGS", // found in `ma_benefits`
  "SERVICE_OUTPATIENT_HOSPITAL_SERVICES",
  "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT",
  "SERVICE_OUTPATIENT_GROUP_THERAPY_VISIT_WITH_PSYCHIATRIST",
  "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT",
  "SERVICE_OUTPATIENT_INDIVIDUAL_THERAPY_VISIT_WITH_PSYCHIATRIST",
  "SERVICE_OUTPATIENT_XRAYS",
  "SERVICE_PART_B_INSULIN",
  "SERVICE_PHYSICAL_THERAPY_AND_SPEECH_AND_LANGUAGE_THERAPY_VISIT",
  "SERVICE_PRIMARY",
  "SERVICE_PROSTHETICS",
  "SERVICE_SPECIALIST",
  "SERVICE_URGENT_CARE",
  "VISION_CONTACT_LENSES",
  "VISION_EYEGLASSES_FRAMES",
  "VISION_EYEGLASSES_FRAMES_AND_LENSES",
  "VISION_EYEGLASSES_LENSES",
  "VISION_ROUTINE_EYE_EXAMS",
  "VISION_UPGRADES",
  "SKILLED_NURSING_FACILITY",
  "INPATIENT_HOSPITAL",
  "POST_DISCHARGE_MED_REC",
  "READMISSION_PREVENTION",
  "WIGS_CHEMO_RELATED",
  "WEIGHT_MANAGEMENT",
  "ADULT_DAY_HEALTH",
  "HOME_PALLIATIVE_CARE",
  "CHIROPRACTIC",
  "ACUPUNCTURE",
  "MASSAGE",
  "ALT_THERAPIES",
  "HEALTH_EDUCATION",
  "COUNSELING",
  "CAREGIVER_SUPPORT",
  "PERS",
  "FITNESS",
  "NON_EMERGENCY_CARE_ANY_HEALTH_LOCATION",
  "NON_EMERGENCY_CARE_PLAN_APPROVED",
  "OTC_ITEMS",
  "IN_HOME_SUPPORT",
  "HOME_BATHROOM_SAFETY",
  "MEALS_SHORT_DURATION",
  "ANNUAL_PHYSICAL",
  "TELEHEALTH",
  "SERVICE_WORLDWIDE_EMERGENCY",
  "FOOD_PRODUCE",
  "MEALS",
  "PEST_CONTROL",
  "TRANSPORTATION_NON_MED_ANY",
  "TRANSPORTATION_NON_MED_PLAN_APPROVED",
  "AIR_QUALITY_EQUIPMENT",
  "SOCIAL_NEEDS",
  "COMPLEMENTARY_THERAPIES",
  "SUPPORT_SELF_DIRECTION",
  "STRUCTURAL_HOME_MODS",
  "GENERAL_SUPPORTS",
];
